import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { qk } from './keys';
import type { ErsEvent } from './types';

/**
 * Реальний час: GET /api/v1/stream (Server-Sent Events).
 * Події «тонкі» (ідентифікатори й змінені поля) — за ними інвалідуються відповідні запити.
 * Читаємо потік через fetch, бо сервер надсилає іменовані події (`event: task.updated`),
 * а EventSource.onmessage їх не отримує; так само модулі хвилі 2 можуть підписатися на будь-який тип.
 */
type Listener = (e: ErsEvent) => void;
const listeners = new Set<Listener>();

export function subscribeEvents(cb: Listener) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'offline';
let status: StreamStatus = 'connecting';
const statusListeners = new Set<() => void>();
function setStatus(s: StreamStatus) {
  if (s === status) return;
  status = s;
  for (const l of statusListeners) l();
}

export function useStreamStatus(): StreamStatus {
  return useSyncExternalStore(
    (cb) => {
      statusListeners.add(cb);
      return () => {
        statusListeners.delete(cb);
      };
    },
    () => status,
    () => status,
  );
}

async function readStream(signal: AbortSignal, onEvent: (e: ErsEvent) => void): Promise<number | undefined> {
  const res = await fetch('/api/v1/stream', {
    credentials: 'include',
    headers: { accept: 'text/event-stream', 'x-ers-client': 'web' },
    signal,
  });
  if (!res.ok || !res.body) {
    if (res.status === 401) throw Object.assign(new Error('unauthorized'), { status: 401 });
    throw new Error(`stream ${res.status}`);
  }
  setStatus('open');
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let retry: number | undefined;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let sep: number;
    while ((sep = buffer.search(/\r?\n\r?\n/)) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep).replace(/^\r?\n\r?\n/, '');
      let data = '';
      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith(':')) continue;
        const idx = line.indexOf(':');
        const field = idx < 0 ? line : line.slice(0, idx);
        const val = idx < 0 ? '' : line.slice(idx + 1).replace(/^ /, '');
        if (field === 'data') data += (data ? '\n' : '') + val;
        else if (field === 'retry' && /^\d+$/.test(val)) retry = Number(val);
      }
      if (data) {
        try {
          onEvent(JSON.parse(data) as ErsEvent);
        } catch {
          /* пошкоджена подія — пропускаємо */
        }
      }
    }
  }
  return retry;
}

/** Інвалідація кешу за подією. Пакетна: кілька подій за 150 мс — одне оновлення. */
function makeInvalidator(qc: QueryClient) {
  const pending = new Set<string>();
  let timer: number | undefined;
  const keys = new Map<string, readonly unknown[]>();
  const add = (key: readonly unknown[]) => {
    const k = JSON.stringify(key);
    pending.add(k);
    keys.set(k, key);
  };
  const flush = () => {
    timer = undefined;
    // Не перебиваємо перетягування на дошці: переліки оновляться після збереження переміщення.
    const moving = qc.isMutating({ mutationKey: ['move-task'] }) > 0;
    for (const k of pending) {
      const key = keys.get(k)!;
      if (moving && key[0] === 'tasks') continue;
      void qc.invalidateQueries({ queryKey: key });
    }
    pending.clear();
  };
  return (e: ErsEvent) => {
    const type = e.type;
    if (type.startsWith('task.') || type === 'dependency.changed' || type === 'approval.decided') {
      add(qk.task(e.entityId));
      add(qk.tasksAll);
      add(qk.mySummary);
      for (const p of e.projectIds) add(qk.project(p));
      add(qk.inboxAll);
    } else if (type === 'comment.created') {
      add(qk.taskPart(e.entityId, 'comments'));
      add(qk.task(e.entityId));
      add(qk.inboxAll);
    } else if (type.startsWith('project.') || type === 'section.changed') {
      add(qk.projectsAll);
      add(qk.project(e.entityId));
      add(qk.tasksAll);
    } else if (type.startsWith('inbox.')) {
      // Нове сповіщення для мене (сервер надсилає його лише отримувачу).
      add(qk.inboxAll);
      add(qk.mySummary);
    } else if (type.startsWith('user.')) {
      add(qk.users);
    } else if (type.startsWith('token.')) {
      add(qk.tokens);
    } else if (type.startsWith('time.')) {
      // Модуль обліку часу: таймер, записи, підсумки за задачею.
      add(['time']);
    }
    if (timer === undefined) timer = window.setTimeout(flush, 150);
  };
}

/** Підключення до потоку подій на час роботи оболонки; автоматичне перепідключення. */
export function useRealtime(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const invalidate = makeInvalidator(qc);
    const ctrl = new AbortController();
    let stopped = false;
    let attempt = 0;
    let timer: number | undefined;

    const connect = async () => {
      if (stopped) return;
      if (!navigator.onLine) {
        setStatus('offline');
        return;
      }
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      let retry: number | undefined;
      try {
        retry = await readStream(ctrl.signal, (e) => {
          invalidate(e);
          for (const l of listeners) l(e);
        });
        attempt = 0;
      } catch (err) {
        if (stopped) return;
        if ((err as { status?: number }).status === 401) {
          void qc.invalidateQueries({ queryKey: qk.me });
          return;
        }
        attempt++;
      }
      if (stopped) return;
      setStatus('reconnecting');
      const delay = Math.min(30_000, (retry ?? 3000) * Math.max(1, attempt));
      timer = window.setTimeout(() => void connect(), delay);
      // Після розриву оновлюємо дані: події під час розриву не повторюються.
      void qc.invalidateQueries({ queryKey: qk.tasksAll });
      void qc.invalidateQueries({ queryKey: qk.inboxAll });
    };

    const onOnline = () => {
      window.clearTimeout(timer);
      attempt = 0;
      void connect();
    };
    const onOffline = () => setStatus('offline');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    void connect();
    return () => {
      stopped = true;
      ctrl.abort();
      window.clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [enabled, qc]);
}
